import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ConfigService } from '@karve.it/core';
import { LocationService } from '@karve.it/features';
import { googleLocationOptions } from '@karve.it/interfaces/estimate';
import { LocationCreateBase } from '@karve.it/interfaces/locations';

import { concatMap, map } from 'rxjs/operators';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';
import { Address, GoogleHelperService } from 'src/app/services/google-helper.service';
import { SubSink } from 'subsink';

import { regex, WatcherEvent, watcherEvents } from '../../global.constants';
import { SetConfigValuesInput } from '../../interfaces/config';
import { safeParseJSON } from '../../js';
import { BrandingService } from '../../services/branding.service';

import { defaultContactDetails, defaultFranchiseInfo, defaultLegalCard, defaultSystemDetails } from '../franchise.interfaces';

const jobWatcherSettings = [
  'notifyWatchersDocumentCompleted',
  'notifyWatchersDocumentSent',
  'notifyWatchersSMSReceived',
  'notifyWatchersEmailReceived',
  'notifyWatchersEmailError',
  'notifyWatchersBCC',
  'notifyWatchersBCCRulesEmails',
  'notifyWatchersBCCManualEmails',
  'notifyWatchersBCCAIEmails',
  'notifyWatchersNewLead',
  'notifyWatchersScheduledCommunicationEscalation',
] as const;

type JobWatcher = {
  email: string;
  events?: WatcherEvent[];
};

@Component({
  selector: 'app-business-general',
  templateUrl: './business-general.component.html',
  styleUrls: ['./business-general.component.scss']
})
export class BusinessGeneralComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('jobWatchers') jobWatchersRef: ElementRef<any>;
  @ViewChild('leadWatchers') leadWatchersRef: ElementRef<any>;

  subs = new SubSink();

  locationForm: UntypedFormGroup = new UntypedFormGroup({
    id: new UntypedFormControl(undefined, {}),
    location: new UntypedFormControl(undefined, {}),
  });
  locationToCreate: LocationCreateBase;

  franchiseInfo: any = { ...defaultFranchiseInfo };

  systemDetails: any = { ...defaultSystemDetails };

  legalCard: any = { ...defaultLegalCard };

  contactDetails: any = { ...defaultContactDetails };

  configsLoaded = false;

  modifiedConfigs: string[] = [];

  options = googleLocationOptions;

  watcherEvents = watcherEvents.map(e => ({ label: e, value: e }));

  notificationsForm = new FormGroup({
    jobWatchers: new FormArray([]),
    leadWatchers: new FormControl<string[]>({ value: [], disabled: true }),

    notifyWatchersDocumentCompleted: new FormControl({value: false, disabled: true}),
    notifyWatchersDocumentSent: new FormControl({value: false, disabled: true}),
    notifyWatchersSMSReceived: new FormControl({value: false, disabled: true}),
    notifyWatchersEmailReceived: new FormControl({value: false, disabled: true}),
    notifyWatchersEmailError: new FormControl({value: false, disabled: true}),
    notifyWatchersBCC: new FormControl({value: false, disabled: true}),
    notifyWatchersBCCRulesEmails: new FormControl({value: false, disabled: true}),
    notifyWatchersBCCManualEmails: new FormControl({value: false, disabled: true}),
    notifyWatchersBCCAIEmails: new FormControl({value: false, disabled: true}),
    notifyWatchersScheduledCommunicationEscalation: new FormControl({value: false, disabled: true}),
    notifyWatchersNewLead: new FormControl({value: false, disabled: true}),

    // deprecated
    leadNotificationRecipients: new FormControl({ value: [], disabled: true }),
  });

  constructor(
    private configService: ConfigService,
    private freyaHelper: FreyaHelperService,
    public googleHelper: GoogleHelperService,
    private locationService: LocationService,
    private localNotify: FreyaNotificationsService,
    private brandingSvc: BrandingService,
    ) { }

  ngOnInit(): void {
    this.retrieveFranchiseConfigs();

    this.subs.sink = this.brandingSvc.currentZone().subscribe((zone) => {
      this.getHQLocation();

      if (zone.id) {
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngAfterViewInit(): void {
    const { hash } = location;
    if (hash !== '#job-watchers') { return; }
    if (!this.jobWatchersRef?.nativeElement) { return; }
    this.jobWatchersRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
    this.leadWatchersRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  async getHQLocation(networkOnly?: boolean){
    const hq = await this.freyaHelper.getDockLocation(networkOnly);
    if(!hq) { return; }
    this.locationForm.setValue({
      id: hq.id,
      location: hq.addressLineOne
    });
  }

  retrieveFranchiseConfigs() {
    this.subs.sink = this.configService.watchConfigValues(
      {
        keys: [
          'franchise-info.*',
          'system-details.*',
          'legal.*',
          'additional-contact-details.*',
          'jobs.watchers',
          'jobs.leadWatchers',
          'intake.notificationRecipients',
          ...jobWatcherSettings.map((setting) => `jobs.${ setting }`)
        ]
      }
    ).valueChanges.subscribe((res) => {
      // Reset Values
      this.franchiseInfo = { ...defaultFranchiseInfo };
      this.systemDetails = { ...defaultSystemDetails };
      this.legalCard = { ...defaultLegalCard };
      this.contactDetails = { ...defaultContactDetails};
      this.jobWatchers.clear();
      this.notificationsForm.controls.leadWatchers.setValue([]);

      for (const setting of jobWatcherSettings) {
        this.notificationsForm.controls[setting].setValue(false);
      }

      for (const config of res.data.getConfigValues) {

        if (config.key === 'jobs.watchers' && config.value) {

          const parsed = this.parseJobWatchers(config.value);
          this.setJobWatchers(parsed);
          this.handleWatchersControls(false);
        }

        if (config.key === 'jobs.leadWatchers' && config.value) {
          this.notificationsForm.controls.leadWatchers.setValue(config.value.split(';'));
        }

        if (config.key === 'intake.notificationRecipients' && config.value) {
          this.notificationsForm.controls.leadNotificationRecipients.setValue(safeParseJSON(config.value, []));
        }

        for (const setting of jobWatcherSettings) {

          if (config.key === `jobs.${ setting }` && config.value) {
            this.notificationsForm.controls[setting].setValue(JSON.parse(config.value));
          }
        }

        const [namespace, key] = config.key.split('.');

        switch (namespace) {
          case 'franchise-info':
            this.franchiseInfo[key] = config.value;
            break;
          case 'system-details':
            this.systemDetails[key] = config.value;
            break;
          case 'legal':
            this.legalCard[key] = config.value;
            break;
          case 'additional-contact-details':
            this.contactDetails[key] = config.value;
            break;
        }

      }
      this.configsLoaded = true;
    });
  }

  saveInfo(namespace: string, data) {
    if (namespace === 'franchise-info'){
      this.saveHQLocation();
    }

    const val = this.notificationsForm.value;

    const values = Object.keys(data)
    .filter((ok) => ok !== 'editMode' && data[ok]) // Only get values from the object being edited
    .filter((ok) => this.modifiedConfigs.find((mc) => mc === ok)) // Only get values that have been edited
    .map((k) => (
      {
        key: `${namespace}.${k}`,
        value: data[k],
      }
    ));

    if (
      namespace === 'additional-contact-details'
      && this.notificationsForm.controls.jobWatchers.dirty
    ) {
      const serialized = this.serializeJobWatchers(val.jobWatchers);

      values.push({
        key: 'jobs.watchers',
        value: serialized,
      });
    }

    if (
      namespace === 'additional-contact-details'
      && this.notificationsForm.controls.leadWatchers.dirty
    ) {
      values.push({
        key: 'jobs.leadWatchers',
        value: val.leadWatchers.join(';'),
      });
    }

    // deprecated
    if (
      namespace === 'additional-contact-details'
      && this.notificationsForm.controls.leadNotificationRecipients.dirty
    ) {
      values.push({
        key: 'intake.notificationRecipients',
        value: JSON.stringify(val.leadNotificationRecipients),
      });
    }

    for (const setting of jobWatcherSettings) {
      if (
        namespace === 'additional-contact-details'
        && this.notificationsForm.controls[setting].touched
      ) {
        values.push({
          key: `jobs.${ setting }`,
          value: JSON.stringify(this.notificationsForm.controls[setting].value),
        });
      }
    }

    data.editMode = false;

    this.onContactDetailsEditModeChange();

    if (!values.length) { return; }

    this.subs.sink = this.configService.setConfigValues({ configs: values }).subscribe((_res) => {
      this.modifiedConfigs = [];
    });
  }

  async saveHQLocation(){
    if (this.locationForm.dirty && !this.locationToCreate){
      this.getHQLocation();
      this.localNotify.warning('HQ Location Reset', 'Use the autocomplete to set the location');
    }
    if (!this.locationToCreate) { return; } // Nothing to save

    this.locationToCreate.attributes = ['ZoneHQ'];

    if (this.locationForm.value.id) {
      await this.locationService.EditLocations({ids: [this.locationForm.value.id], edit: {attributes: ['old-zone-hq']}}).toPromise();
    }

    // Get and Set the Coordinates for this location
    // const geocodingResult = await this.googleHelper.geocodeAddress({ formattedAddress: this.locationToCreate.addressLineOne});
    // this.locationToCreate.coordinates = this.googleHelper.getCoordinatesFromGeometry(geocodingResult.geometry);

    this.locationService.CreateLocations({input: {locations: [this.locationToCreate]}})
      .pipe(
        map((res) => res.data.createLocations.locations[0].id as string),
        map((newLocationId) => ({  configs: [{key: 'franchise-info.dockLocationId', value: newLocationId }] } as SetConfigValuesInput)),
        concatMap((input) => this.configService.setConfigValues(input))
      ).subscribe(() => this.getHQLocation(true));
  }

  parseJobWatchers(value: string): JobWatcher[] {
    if (!value) return [];
    return value.split(';').map(item => {
      const [email, events] = item.split(':');
      return {
        email: email.trim(),
        events: events ? events.split(',').map(e => e.trim() as WatcherEvent) : []
      };
    });
  }

  serializeJobWatchers(watchers: JobWatcher[]): string {
    return watchers.map(w =>
      w.events.length ? `${w.email}:${w.events.join(',')}` : w.email
    ).join(';');
  }

  get jobWatchers(): FormArray {
    return this.notificationsForm.get('jobWatchers') as FormArray;
  }

  createWatcherGroup(email = '', events: WatcherEvent[] = []): FormGroup {
    return new FormGroup({
      email: new FormControl({ value: email, disabled: false }),
      events: new FormControl(events)
    });
  }

  addWatcher(event: KeyboardEvent) {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const email = input.value.trim();

    if (!email) return;

    const fa = this.jobWatchers;
    if (fa.value.some((w: JobWatcher) => w.email === email)) {
      input.value = '';
      return;
    }

    fa.push(this.createWatcherGroup(email, []));
    this.jobWatchers.markAsDirty();
    this.markAsModified('jobs.watchers');

    input.value = '';
  }

  removeWatcher(index: number) {
    const fa = this.jobWatchers;
    fa.removeAt(index);
    this.jobWatchers.markAsDirty();
    this.markAsModified('jobs.watchers');
  }

  setJobWatchers(watchers: JobWatcher[]) {
    const fa = this.jobWatchers;
    fa.clear();
    watchers.forEach(w => {
      fa.push(new FormGroup({
        email: new FormControl(w.email),
        events: new FormControl(w.events)
      }));
    });
  }

  markAsModified(key: string){
    this.modifiedConfigs.push(key);
  }

  identify(index, item) {
    return item.key;
  }

  checkJobWatchers(formControlName: string) {
    const jobWatchers = (this.notificationsForm.value[formControlName] || []).filter((watcher) => {
      const isEmail = watcher.match(regex.email);
      const isPhone = watcher.match(regex.phone);
      if (isEmail || isPhone) {
        return true;
      }

      this.localNotify.warning(`Could not add ${ formControlName }`, `${ watcher } is not a valid email or phone number.`);

      return false;

    });

    this.notificationsForm.controls[formControlName].setValue(jobWatchers);
  }

  handleAddressChange(address: Address){
    if(!this.googleHelper.isValidGooglePlacesAddress(address)){
      this.locationForm.controls.location.setErrors({notvalid: true});
    }

    this.locationToCreate = this.googleHelper.convertGoogleLocationToCreateLocationInput(address);

    this.locationForm.controls.location.setErrors(null);
  }

  onContactDetailsEditModeChange() {
    if (this.contactDetails.editMode) {
      this.notificationsForm.enable();
      this.handleBCCControl();
      this.handleWatchersControls(true);
    } else {
      this.notificationsForm.disable();
      this.handleWatchersControls(false);
    }
  }

  handleWatchersControls(enable: boolean){
    if (enable) {
      this.notificationsForm.get('jobWatchers')?.enable();
    } else {
      this.notificationsForm.get('jobWatchers')?.disable();
    }
  }

  handleBCCControl(): void {
    const subSwitches = [
      'notifyWatchersBCCRulesEmails',
      'notifyWatchersBCCAIEmails',
      'notifyWatchersBCCManualEmails'
    ];

    this.notificationsForm.get('notifyWatchersBCC')?.valueChanges.subscribe((bccEnabled: boolean) => {
      if (bccEnabled) {
        this.disableControls(subSwitches);
      } else {
        this.enableControls(subSwitches);
      }
    });
  }

  disableControls(controls: string[]): void {
    controls.forEach(control => {
      const formControl = this.notificationsForm.get(control);
      if (formControl) {
        formControl.disable({ emitEvent: false });
      }
    });
  }

  enableControls(controls: string[]): void {
    controls.forEach(control => {
      const formControl = this.notificationsForm.get(control);
      if (formControl) {
        formControl.enable({ emitEvent: false });
      }
    });
  }
}
